import React, { useState, useEffect } from 'react';
import api from '../../lib/api';

const ConfiguratorAdmin = () => {
  const [productTypes, setProductTypes] = useState([]);
  const [selectedProductType, setSelectedProductType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editingValue, setEditingValue] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await api.get('/configurator/admin/all');
      setProductTypes(response.data.productTypes);
      if (response.data.productTypes.length > 0) {
        setSelectedProductType(response.data.productTypes[0]);
      }
    } catch (err) {
      console.error('Failed to load admin data', err);
    }
    setLoading(false);
  };

  const handleSaveProductType = async (data) => {
    try {
      if (data.id) {
        await api.put(`/configurator/product-types/${data.id}`, data);
      } else {
        await api.post('/configurator/product-types', data);
      }
      loadData();
    } catch (err) {
      alert('Failed to save product type');
    }
  };

  const handleSaveOptionGroup = async (data) => {
    try {
      if (data.id) {
        await api.put(`/configurator/option-groups/${data.id}`, data);
      } else {
        await api.post('/configurator/option-groups', data);
      }
      loadData();
    } catch (err) {
      alert('Failed to save option group');
    }
  };

  const handleSaveOptionValue = async (data) => {
    try {
      if (data.id) {
        await api.put(`/configurator/option-values/${data.id}`, data);
      } else {
        await api.post('/configurator/option-values', data);
      }
      loadData();
    } catch (err) {
      alert('Failed to save option value');
    }
  };

  const handleDeleteOptionGroup = async (id) => {
    if (!window.confirm('Are you sure you want to delete this group?')) return;
    try {
      await api.delete(`/configurator/option-groups/${id}`);
      loadData();
    } catch (err) {
      alert('Failed to delete option group');
    }
  };

  const handleDeleteOptionValue = async (id) => {
    if (!window.confirm('Are you sure you want to delete this option?')) return;
    try {
      await api.delete(`/configurator/option-values/${id}`);
      loadData();
    } catch (err) {
      alert('Failed to delete option value');
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Configurator Admin</h1>

      {/* Product Types */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold mb-4">Product Types</h2>
        <div className="space-y-4">
          {productTypes.map(pt => (
            <div key={pt.id} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold">{pt.name} ({pt.key})</h3>
                  <p className="text-sm text-gray-400">{pt.description}</p>
                  <p className="text-sm text-green-400">Base: €{pt.base_price_hour}/hr</p>
                </div>
                <button
                  onClick={() => setSelectedProductType(pt)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm"
                >
                  Edit Options
                </button>
              </div>
            </div>
          ))}

          {/* Add Product Type Form */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <h3 className="font-semibold mb-4">Add New Product Type</h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                handleSaveProductType({
                  key: formData.get('key'),
                  name: formData.get('name'),
                  description: formData.get('description'),
                  base_price_hour: parseFloat(formData.get('base_price_hour')),
                });
                e.target.reset();
              }}
              className="grid grid-cols-2 gap-4"
            >
              <input name="key" placeholder="Key (e.g., cloudpc)" className="bg-gray-700 p-2 rounded" required />
              <input name="name" placeholder="Name (e.g., Cloud PC)" className="bg-gray-700 p-2 rounded" required />
              <input name="description" placeholder="Description" className="bg-gray-700 p-2 rounded" />
              <input name="base_price_hour" type="number" step="0.01" placeholder="Base price/hour" className="bg-gray-700 p-2 rounded" required />
              <button type="submit" className="col-span-2 bg-green-600 hover:bg-green-700 py-2 rounded">
                Add Product Type
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Option Groups & Values */}
      {selectedProductType && (
        <div>
          <h2 className="text-lg font-semibold mb-4">
            Options for {selectedProductType.name}
          </h2>

          {selectedProductType.optionGroups?.map(group => (
            <div key={group.id} className="bg-gray-800 rounded-lg p-4 border border-gray-700 mb-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold">
                    {group.name} ({group.key})
                    {group.required && <span className="text-red-400 ml-2">*Required</span>}
                  </h3>
                  <p className="text-sm text-gray-400">{group.description}</p>
                  <p className="text-sm text-gray-500">Sort: {group.sort_order} | Type: {group.input_type}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingGroup({ ...group, mode: 'edit' })}
                    className="px-3 py-1 bg-yellow-600 hover:bg-yellow-700 rounded text-sm"
                  >
                    Edit Group
                  </button>
                  <button
                    onClick={() => setEditingValue({ option_group_id: group.id, mode: 'add' })}
                    className="px-3 py-1 bg-green-600 hover:bg-green-700 rounded text-sm"
                  >
                    Add Option
                  </button>
                  <button
                    onClick={() => handleDeleteOptionGroup(group.id)}
                    className="px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* Option Values List */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {group.optionValues?.map(value => (
                  <div
                    key={value.id}
                    className={`p-3 rounded border ${
                      value.available
                        ? 'bg-gray-700 border-gray-600'
                        : 'bg-gray-900 border-gray-800 opacity-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{value.label}</div>
                        <div className="text-sm text-gray-400">{value.value}</div>
                        {value.subtitle && (
                          <div className="text-xs text-gray-500">{value.subtitle}</div>
                        )}
                        <div className="text-sm text-green-400">
                          {parseFloat(value.price_delta_hour) > 0
                            ? `+€${value.price_delta_hour}/hr`
                            : 'Included'}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => setEditingValue({ ...value, mode: 'edit' })}
                          className="p-1 text-yellow-400 hover:text-yellow-300"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteOptionValue(value.id)}
                          className="p-1 text-red-400 hover:text-red-300"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Add Option Group Button */}
          <button
            onClick={() => setEditingGroup({ product_type_id: selectedProductType.id, mode: 'add' })}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 rounded-lg mb-4"
          >
            + Add Option Group
          </button>
        </div>
      )}

      {/* Edit Option Group Modal */}
      {editingGroup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingGroup.mode === 'add' ? 'Add' : 'Edit'} Option Group
            </h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                handleSaveOptionGroup({
                  id: editingGroup.id,
                  product_type_id: editingGroup.product_type_id,
                  key: formData.get('key'),
                  name: formData.get('name'),
                  description: formData.get('description'),
                  sort_order: parseInt(formData.get('sort_order')),
                  required: formData.get('required') === 'on',
                  multiple: formData.get('multiple') === 'on',
                  input_type: formData.get('input_type'),
                });
                setEditingGroup(null);
              }}
              className="space-y-4"
            >
              <input
                name="key"
                defaultValue={editingGroup.key}
                placeholder="Key (e.g., gpu)"
                className="w-full bg-gray-700 p-2 rounded"
                required
              />
              <input
                name="name"
                defaultValue={editingGroup.name}
                placeholder="Display Name (e.g., GPU)"
                className="w-full bg-gray-700 p-2 rounded"
                required
              />
              <textarea
                name="description"
                defaultValue={editingGroup.description}
                placeholder="Description"
                className="w-full bg-gray-700 p-2 rounded"
              />
              <input
                name="sort_order"
                type="number"
                defaultValue={editingGroup.sort_order || 0}
                placeholder="Sort Order"
                className="w-full bg-gray-700 p-2 rounded"
              />
              <select
                name="input_type"
                defaultValue={editingGroup.input_type || 'dropdown'}
                className="w-full bg-gray-700 p-2 rounded"
              >
                <option value="dropdown">Dropdown</option>
                <option value="cards">Cards (GPU style)</option>
                <option value="radio">Radio</option>
                <option value="checkbox">Checkbox (Multiple)</option>
              </select>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="required"
                  defaultChecked={editingGroup.required}
                />
                Required
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="multiple"
                  defaultChecked={editingGroup.multiple}
                />
                Allow Multiple Selection
              </label>
              <div className="flex gap-2">
                <button type="submit" className="flex-1 py-2 bg-green-600 rounded">
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setEditingGroup(null)}
                  className="flex-1 py-2 bg-gray-600 rounded"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Option Value Modal */}
      {editingValue && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingValue.mode === 'add' ? 'Add' : 'Edit'} Option Value
            </h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                handleSaveOptionValue({
                  id: editingValue.id,
                  option_group_id: editingValue.option_group_id,
                  label: formData.get('label'),
                  value: formData.get('value'),
                  subtitle: formData.get('subtitle'),
                  note: formData.get('note'),
                  price_delta_hour: parseFloat(formData.get('price_delta_hour')) || 0,
                  sort_order: parseInt(formData.get('sort_order')) || 0,
                  available: formData.get('available') === 'on',
                });
                setEditingValue(null);
              }}
              className="space-y-4"
            >
              <input
                name="label"
                defaultValue={editingValue.label}
                placeholder="Display Label (e.g., RTX 4090)"
                className="w-full bg-gray-700 p-2 rounded"
                required
              />
              <input
                name="value"
                defaultValue={editingValue.value}
                placeholder="Value (e.g., rtx-4090)"
                className="w-full bg-gray-700 p-2 rounded"
                required
              />
              <input
                name="subtitle"
                defaultValue={editingValue.subtitle}
                placeholder="Subtitle (e.g., similar to RTX 4090)"
                className="w-full bg-gray-700 p-2 rounded"
              />
              <input
                name="note"
                defaultValue={editingValue.note}
                placeholder="Note (e.g., Not available)"
                className="w-full bg-gray-700 p-2 rounded"
              />
              <input
                name="price_delta_hour"
                type="number"
                step="0.01"
                defaultValue={editingValue.price_delta_hour || 0}
                placeholder="Price Delta/hour"
                className="w-full bg-gray-700 p-2 rounded"
              />
              <input
                name="sort_order"
                type="number"
                defaultValue={editingValue.sort_order || 0}
                placeholder="Sort Order"
                className="w-full bg-gray-700 p-2 rounded"
              />
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="available"
                  defaultChecked={editingValue.available !== false}
                />
                Available
              </label>
              <div className="flex gap-2">
                <button type="submit" className="flex-1 py-2 bg-green-600 rounded">
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setEditingValue(null)}
                  className="flex-1 py-2 bg-gray-600 rounded"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfiguratorAdmin;
